import { hasPermission } from "./matching";
import {
  getLegacyRolesForOrg,
  hasOrgAccess,
  isPlatformAdmin,
  permissionsFromPayload,
} from "./jwt";
import { hasLegacyRole } from "./legacy-roles";
import type { AuthTokenPayload } from "./types";

export interface CanAccessOptions {
  orgId?: string | null;
  legacyRoles?: readonly string[];
}

/**
 * Dual-mode access check: platform admin bypass, new flat permission, then legacy role fallback.
 */
export function canAccess(
  payload: AuthTokenPayload | null | undefined,
  permissionList: readonly string[],
  requiredPermission: string,
  options: CanAccessOptions = {},
): boolean {
  if (isPlatformAdmin(payload)) {
    return true;
  }

  const { orgId, legacyRoles = [] } = options;

  if (permissionList.length > 0 && hasPermission(permissionList, requiredPermission)) {
    return true;
  }

  if (orgId && legacyRoles.length > 0) {
    const roles = getLegacyRolesForOrg(payload, orgId);
    if (hasLegacyRole(roles, legacyRoles)) {
      return true;
    }
  }

  return false;
}

export function canAccessOrg(
  payload: AuthTokenPayload | null | undefined,
  permissionList: readonly string[],
  orgId: string | null | undefined,
): boolean {
  if (isPlatformAdmin(payload)) {
    return true;
  }
  if (!orgId) {
    return false;
  }
  if (hasOrgAccess(permissionList, orgId, payload)) {
    return true;
  }
  const legacyRoles = getLegacyRolesForOrg(payload, orgId);
  return legacyRoles.length > 0;
}

export function resolvePermissionList(
  payload: AuthTokenPayload | null | undefined,
  mePermissions?: string[] | Record<string, string[]> | null,
): string[] {
  let tenantFlat: string[] = [];
  if (Array.isArray(mePermissions) && mePermissions.length > 0) {
    tenantFlat = mePermissions.filter(Boolean).map(String);
  } else {
    tenantFlat = permissionsFromPayload(payload);
  }

  const platformPerms = payload?.platform_permissions ?? [];
  if (platformPerms.length === 0) {
    return tenantFlat;
  }
  return [...new Set([...tenantFlat, ...platformPerms])];
}
